/* eslint-disable max-len */
/**
 * Utility functions for processing multi-company user data
 * Handles company selection and user session data normalization
 */

interface CompanyData {
	companyId: string;
	companyName?: string;
	userId?: string;
	role?: string;
	empRole?: string;
	isActive?: string;
	[key: string]: any;
}

interface CognitoUserPayload {
	attributes: {
		sub: string;
		email: string;
		'custom:contactPerson'?: string;
		'custom:fullName'?: string;
		'custom:companyData'?: string;
		'custom:industryTypes'?: string;
		'custom:thumbnailImage'?: string;
		'custom:profileImage'?: string;
		'custom:companyProfileImage'?: string;
		'custom:companyThumbImage'?: string;
		'custom:firstName'?: string;
		[key: string]: any;
	};
}

interface ApiUserResponse {
	data: {
		userId?: string;
		email?: string;
		firstName?: string;
		companyData?: CompanyData[];
		industryTypes?: string;
		thumbnailImage?: string;
		profileImage?: string;
		companyProfileImage?: string;
		companyThumbImage?: string;
		[key: string]: any;
	};
	status?: boolean;
}

interface ProcessedUserData {
	masterId: string;
	userId: string;
	firstName: string;
	email: string;
	companyName?: string;
	role?: string;
	companyId?: string;
	empRole?: string;
	isActive?: string;
	industryTypes?: string;
	thumbnailImage?: string;
	profileImage?: string;
	companyProfileImage?: string;
	companyThumbImage?: string;
	companyList?: CompanyData[];
	uuid?: string;
	[key: string]: any;
}

/**
 * Get the defaultselected company from a list of companies
 * Priority order:
 * 1. Company matching selectedCompanyId (if provided)
 * 2. First active company (isActive === 'active')
 * 3. First company in the list
 * @param companyList - Array of company data
 * @param selectedCompanyId - Optional company ID from localStorage
 * @returns Selected company data
 */
export const getDefaultSelectedCompany = (
	companyList: CompanyData[],
	selectedCompanyId?: string | null
): CompanyData | null =>
{
	if (!companyList || !Array.isArray(companyList) || companyList.length === 0)
	{
		return null;
	}

	// Priority 1: If a specific company/account is selected and is active, return it
	if (selectedCompanyId)
	{
		let foundCompany: CompanyData | undefined;

		// Check if 'individual' marker is set - find individual account
		if (selectedCompanyId === 'individual')
		{
			foundCompany = companyList.find(
				(company) => company?.role === 'individual'
			);
		}
		else
		{
			// Find by companyId for company accounts
			foundCompany = companyList.find(
				(company) => company?.companyId === selectedCompanyId
			);
		}

		// Only return if the account is active
		if (foundCompany && foundCompany?.isActive === 'active')
		{
			return foundCompany;
		}
	}

	// Priority 2: Find first active company/account
	const activeCompany = companyList.find(
		(company) => company?.isActive === 'active'
	);

	if (activeCompany)
	{
		return activeCompany;
	}

	// Priority 3: Default to first company
	return companyList[0];
};

/**
 * Get selected company ID from localStorage
 * @returns Selected company ID or null
 */
export const getSelectedCompanyId = (): string | null =>
{
	return localStorage.getItem('selectedCompanyId');
};

/**
 * Set selected company ID in localStorage
 * @param companyId - Company ID to store
 */
export const setSelectedCompanyId = (companyId: string): void =>
{
	localStorage.setItem('selectedCompanyId', companyId);
};

/**
 * Parse company data from Cognito user attributes
 * @param cognitoPayload - Cognito user payload
 * @returns Processed user data with selected company
 */
export const processCognitoUserData = (cognitoPayload: CognitoUserPayload): ProcessedUserData =>
{
	const { attributes } = cognitoPayload;

	// Parse company data
	const companyList: CompanyData[] = JSON.parse(
		attributes['custom:companyData'] || '[]'
	);

	// Get selected company
	const selectedCompanyId = getSelectedCompanyId();
	const selectedCompany:any = getDefaultSelectedCompany(companyList, selectedCompanyId);

	// Build user data object
	const userData: ProcessedUserData = {
		masterId            : attributes.sub,
		// firstName : attributes['custom:contactPerson'] === '0'
		// 	? attributes['custom:fullName'] || ''
		// 	: attributes['custom:contactPerson'] || '',
		name                : attributes['custom:fullName'] || '',
		firstName           : attributes['custom:fullName'] || '',
		email               : attributes.email,
		companyName         : selectedCompany?.companyName,
		role                : selectedCompany?.role,
		companyId           : selectedCompany?.companyId,
		empRole             : selectedCompany?.empRole,
		isActive            : selectedCompany?.isActive,
		userId              : selectedCompany?.userId || '',
		industryTypes       : attributes['custom:industryTypes'],
		thumbnailImage      : attributes['custom:thumbnailImage'],
		profileImage        : attributes['custom:profileImage'],
		companyProfileImage : attributes['custom:companyProfileImage'],
		companyThumbImage   : attributes['custom:companyThumbImage'],
		companyList
	};

	// if (!userData.companyId && userData.role !== 'individual')
	// {
	// 	// for backward compatibility, will remove later
	// 	userData.companyName = attributes['custom:companyName'];
	// 	userData.role = attributes['custom:role'];
	// 	userData.companyId = attributes['custom:companyId'];
	// 	userData.empRole = attributes['custom:empRole'];
	// 	userData.isActive = attributes['custom:isActive'];
	// }

	return userData;
};

/**
 * Process user data from API response
 * @param apiResponse - API response containing user data
 * @returns Processed user data with selected company
 */
export const processApiUserData = (
	apiResponse: ApiUserResponse
): ApiUserResponse =>
{
	const { data } = apiResponse;

	if (!data?.companyData ||
        !Array.isArray(data.companyData) || data.companyData.length === 0)
	{
		return apiResponse;
	}

	const { companyData } = data;

	delete data.companyData;
	// remove companyData, it is overriding the companyData key when user is in myprofile
	// Get selected company
	const selectedCompanyId = getSelectedCompanyId();
	const selectedCompany = getDefaultSelectedCompany(companyData, selectedCompanyId);

	// If no company was selected before, set the first one as default
	if (!selectedCompanyId && selectedCompany?.companyId)
	{
		setSelectedCompanyId(selectedCompany.companyId);
	}

	// Restructure response to include both companyList and selected company data
	const processedResponse: ApiUserResponse = {
		...apiResponse,
		data : {
			...data,
			masterId    : data?.masterId || data?.userId,
			companyList : companyData,
			...selectedCompany
		}
	};

	return processedResponse;
};

/**
 * Parse company data array from string or return as-is if already array
 * @param companyDataSource - Company data as string or array
 * @returns Parsed company data array
 */
export const parseCompanyData = (
	companyDataSource: string | CompanyData[] | undefined
): CompanyData[] =>
{
	if (!companyDataSource)
	{
		return [];
	}

	if (typeof companyDataSource === 'string')
	{
		try
		{
			return JSON.parse(companyDataSource);
		}
		catch (error)
		{
			console.error('Failed to parse company data:', error);

			return [];
		}
	}

	if (Array.isArray(companyDataSource))
	{
		return companyDataSource;
	}

	return [];
};

/**
 * Get company-specific header for API calls
 * @param companyId - Company ID to include in header
 * @returns Header object with x-company-id
 */
export const getCompanyHeader = (companyId?: string): { 'x-company-id'?: string } | {} =>
{
	return companyId ? { 'x-company-id': companyId } : {};
};

/**
 * Utility for managing user-specific company preferences in localStorage
 * Preferences persist across logout/login sessions
 */

export interface CompanyPreference
{
	masterId: string;
	companyId: string;
}

/**
 * Save user's company preference
 * @param masterId - Unique user identifier (masterId)
 * @param companyId - Company ID to save as preference
 * @returns boolean indicating success
 */
export const saveUserCompanyPreference = (masterId: string, companyId: string): boolean =>
{
	if (!masterId || !companyId)
	{
		return false;
	}

	try
	{
		const key = 'companyPreference';
		const preference: CompanyPreference = {
			masterId,
			companyId
		};

		localStorage.setItem(key, JSON.stringify(preference));

		return true;
	}
	catch (error)
	{
		console.error('Failed to save company preference:', error);

		return false;
	}
};

/**
 * Retrieve user's company preference
 * @returns CompanyPreference object or null if not found
 */
export const getUserCompanyPreference = (): CompanyPreference | null =>
{
	try
	{
		const key = 'companyPreference';
		const stored = localStorage.getItem(key);

		if (!stored)
		{
			return null;
		}

		return JSON.parse(stored) as CompanyPreference;
	}
	catch (error)
	{
		console.error('Failed to retrieve company preference:', error);

		return null;
	}
};
